import React, {
  ComponentProps,
  FocusEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from '@emotion/styled';
import TextFieldBase from '@mui/material/TextField';
import { SpacingProps, spacing } from '@mui/system';

import { isNonEmptyString } from '~/src/common/utilities';
import { useIsSSR } from '~/src/components/hooks/useIsSSR';
import { copper, darkGrey, greyishBlue } from '~/styles';

function useHasUserInteracted() {
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  useEffect(() => {
    const handler = () => {
      setHasUserInteracted(true);
      document.body.removeEventListener('click', handler);
      document.body.removeEventListener('focus', handler);
      document.body.removeEventListener('keydown', handler);
      document.body.removeEventListener('pointerdown', handler);
      document.body.removeEventListener('touchstart', handler);
      document.body.removeEventListener('wheel', handler);
    };
    document.body.addEventListener('click', handler);
    document.body.addEventListener('focus', handler);
    document.body.addEventListener('keydown', handler);
    document.body.addEventListener('pointerdown', handler);
    document.body.addEventListener('touchstart', handler);
    document.body.addEventListener('wheel', handler);
  }, []);
  return hasUserInteracted;
}

// https://mui.com/material-ui/react-text-field/#shrink
/* MUI "shrink" doesn't work with SSR properly
     This cmt is a hack to get "shrink" to work.
     NOTE: derived state doesn't work
   */
/*
 * NOTE: 2024-06-25 impossible to detect input autofill cross browser.
 * the solutions with "-webkit-autofill" doesn't work in the latest chrome versions
 * The current approach to solve label text overlapping is to shrink it until the first user interation
 * and then based on the input value decide do we need shrink or not.
 * In other words we set "shrink" as "true" by default and make it "false" if we're sure that it's safe
 **/
const TextField: React.FC<ComponentProps<typeof TextFieldBase>> = props => {
  const [shrink, setShrink] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const textFieldRef = useRef<HTMLDivElement>(null);
  const isSSR = useIsSSR();
  const hasUserInteracted = useHasUserInteracted();

  useEffect(() => {
    if (!hasUserInteracted) {
      setShrink(true);
    } else if (!isInitialized) {
      const isFocused =
        textFieldRef.current !== null &&
        document.activeElement === textFieldRef.current.querySelector('input');
      const isPrefilled = isNonEmptyString(props.value);

      setShrink(isPrefilled || isFocused);
      setIsInitialized(true);
    }
  }, [hasUserInteracted, isInitialized, props.value]);

  const onBlur = useCallback(
    (params: Parameters<FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>>[0]) => {
      if (typeof props?.onBlur === 'function') {
        props.onBlur(params);
      }
      setShrink(isNonEmptyString(props.value));
    },
    [props],
  );

  const onFocus = useCallback(
    (params: Parameters<FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>>[0]) => {
      if (typeof props?.onFocus === 'function') {
        props.onFocus(params);
      }
      setShrink(true);
    },
    [props],
  );

  const overrideShrink = isSSR ? true : shrink;

  return (
    <TextFieldBase
      {...props}
      onFocus={onFocus}
      onBlur={onBlur}
      InputLabelProps={{ shrink: overrideShrink }}
      ref={textFieldRef}
    />
  );
};

export const FormInput = styled(TextField)`
  ${spacing};
  border-radius: 1px;
  margin-bottom: 7px;
  margin-top: 7px;

  .Mui-focused {
    font-weight: 500;
    color: ${copper};
  }

  .MuiInputBase-adornedEnd {
    cursor: pointer;
    background-color: ${greyishBlue};
  }

  .MuiInputBase-input:focus {
    font-weight: 500;
    color: ${darkGrey};
  }

  .MuiInputBase-root.Mui-disabled:before {
    border: none;
  }

  .MuiInputBase-root.Mui-disabled {
    background: #e6e6e6 0% 0% no-repeat padding-box;
    opacity: 0.6;
  }
  .MuiInputBase-root.Mui-disabled:hover {
    background: #e6e6e6 0% 0% no-repeat padding-box;
  }
  .MuiFilledInput-underline:after {
    border-bottom: 2px solid ${copper};
  }
  .MuiFilledInput-underline:before {
    border-bottom: none;
  }
  .MuiInputLabel-root {
    color: #26292d8a;
  }
` as React.FC<SpacingProps & React.ComponentProps<typeof TextField>>;
